import React, { useState } from "react";
import {
  Button,
  Grid,
  Typography,
  Box,
  TextField,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  ToggleButton,
} from "@mui/material";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { observer } from "mobx-react-lite";

import PaymentMethodDialog from "../dialogs/PaymentMethodDialog";
import { BillFullScreenDialog } from "../dialogs/BillFullScreenDialog";

// Hooks & store
import { useInsuranceBills } from "../hooks/useInsuranceBills";

// Types

import { InsuranceBillsTable } from "./InsuranceBillsTable";
import { InsuranceBillsPagination } from "./InsuranceBillsPagination";
import { BillingTypeEnum, InsuranceBillsContainerProps } from "../types/insuranceBills.types";
import useStore from "../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../stores/Transactions/Transactions.provider";
import { TopLevelPaper } from "../../../../../themes/StyledComponents";
import { BillingStatus } from "../pages/InsurancePageLayout";
import { DeleteConfirmationDialog } from "../../DirectBilling/common/exports";
import PaperEditFullScreen from "../../PaperBilling/Edit/pages/PaperEditFullScreen";
import { WaitingBackdrop } from "../../../Base/components/BackDrop";
import EditCopaymentFullScreen from "../../Copayment/Edit/pages/CopaymentEdit";
import EditFullScreen from "../../DirectBilling/Edit/pages/EditFullScreen";
import GkvEditPage from "../../DirectBilling/GKVEdit/components/GkvEditPage";
import EditCorrectionBillFullScreen from "../../CorrectionBilling/Edit/pages/CorrectionBillEdit";
import EditLineItemCorrectionBillFullScreen from "../../LineItemCorrectionBilling/Edit/pages/EditLineItemCorrectionBillFullScreen";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { commonStyles } from "../../../Kasse/v2/pages/TransactionsOverviewContainer";

export const InsuranceBillsContainer: React.FC<InsuranceBillsContainerProps> = observer(
  ({ billStatus, billingType = BillingTypeEnum.GKV }) => {
    // State for PaymentMethodDialog
    const [openPaymentDialog, setOpenPaymentDialog] = useState<boolean>(false);

    // console.log("InsuranceBillsContainer rendered", billingType);
    // State for edit dialog
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editBills, setEditBills] = useState<any[]>([]);
    // Bill IDs for the new GKVEdit component (fetches its own data)
    const [editBillIds, setEditBillIds] = useState<string[]>([]);

    // Selected bills for editing
    const [selectedBillIds, setSelectedBillIds] = useState<string[]>([]);

    //State for delete dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [transactionIdToDelete, setTransactionIdToDelete] = useState<string | null>(null);

    // State for loading spinner
    const [isLoading, setIsLoading] = useState(false);

    // State for delayed-only filter (OPEN and PREVIOUS)
    const [showDelayedOnly, setShowDelayedOnly] = useState(false);

    // Store
    const transactionStore = useStore(TransactionsContext);
    const {
      getAllPaperInsuranceBill,
      getAllInsuranceBill,
      getAllCorrectedInsuranceBill,
      getAllLineItemCorrectedInsuranceBill,
      lastUpdate,
      transactionRefresh,
      getBillingDataForEditing,
      softDeleteTransaction,
      getAllCopaymentInsuranceBill,
      setStatusOpenToFinished,
      regenerateInsuranceTransactionPdfs,
    } = transactionStore;

    // Use the custom hook to manage fetching, pagination, date filtering
    const {
      allBill,
      searchValue,
      paginatedData,
      page,
      limit,
      setPage,
      setLimit,
      currentRange,
      fetchAllBill,
      handleStartDateChange,
      handleEndDateChange,
      setSearchValue,
    } = useInsuranceBills({
      getAllPaperInsuranceBill,
      getAllInsuranceBill,
      getAllCopaymentInsuranceBill,
      getAllCorrectedInsuranceBill,
      getAllLineItemCorrectedInsuranceBill,
      billStatus,
      lastUpdate,
      transactionRefresh,
      billType: billingType,
    });

    const handleNewRechnung = () => {
      setOpenPaymentDialog(true);
    };

    const handleClosePaymentDialog = () => {
      setOpenPaymentDialog(false);
    };

    // Edit selected bills
    const handleSubmissionForEdit = async () => {
      // GKV uses new GkvEditPage that fetches its own data
      if (billingType === BillingTypeEnum.GKV) {
        setEditBillIds(selectedBillIds);
        setIsDialogOpen(true);
        setSelectedBillIds([]);
        return;
      }

      try {
        setIsLoading(true);
        const bills = await getBillingDataForEditing(selectedBillIds, billingType);
        setEditBills(bills);
        setIsDialogOpen(true);
        setSelectedBillIds([]);
      } catch (error) {
        console.error("Error fetching billing data for editing:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const handleDialogClose = (skipWarning: boolean = false) => {
      if (skipWarning) {
        setIsDialogOpen(false);
        setEditBills([]);
        setEditBillIds([]);
        fetchAllBill();
        return;
      }
    };

    // 2. Handler to open the dialog with the transaction ID
    const handleRequestDelete = (id: string) => {
      setTransactionIdToDelete(id);
      setDeleteDialogOpen(true);
    };

    const handleCancelDelete = () => {
      setDeleteDialogOpen(false);
      setTransactionIdToDelete(null);
    };

    // 4. Confirm deletion
    const handleConfirmDelete = async () => {
      if (!transactionIdToDelete) return;
      try {
        await softDeleteTransaction(transactionIdToDelete);
        fetchAllBill();
      } catch (error) {
        console.error("Error soft-deleting transaction:", error);
      }

      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setTransactionIdToDelete(null);
    };

    // Table selection logic
    const handleSelectAll = (checked: boolean) => {
      if (checked) {
        const newSelecteds = allBill.map((n) => n.id);
        setSelectedBillIds(newSelecteds);
      } else {
        setSelectedBillIds([]);
      }
    };

    const handleSelectOne = (id: string) => {
      setSelectedBillIds((prev) => {
        const isSelected = prev.includes(id);
        if (isSelected) {
          return prev.filter((item) => item !== id);
        }
        return [...prev, id];
      });
    };

    const handleEditOne = async (id: string) => {
      // GKV uses new GkvEditPage that fetches its own data
      if (billingType === BillingTypeEnum.GKV) {
        setEditBillIds([id]);
        setIsDialogOpen(true);
        return;
      }

      try {
        setIsLoading(true);
        const bills = await getBillingDataForEditing([id], billingType);
        setEditBills(bills);
        setIsDialogOpen(true);
      } catch (error) {
        console.error("Error fetching billing data for editing:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const handleStatusChange = async (id: any) => {
      try {
        setIsLoading(true);
        await setStatusOpenToFinished(id);
        fetchAllBill();
      } catch (error) {
        console.error("Error changing status to finished:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Handle PDF regeneration
    const handleRegeneratePdfs = async (billId: string, date: string) => {
      try {
        await regenerateInsuranceTransactionPdfs(billId, date);
        fetchAllBill(); // Refresh the table data to show updated PDFs
      } catch (error) {
        console.error("Error regenerating PDFs:", error);
        throw error; // Re-throw to let the table component handle loading states
      }
    };

    const filteredBills = showDelayedOnly ? allBill.filter((bill) => bill.isDelayed) : allBill;

    let titleText: string;

    if (billingType === BillingTypeEnum.GKV) {
      titleText = "GKV digitale Abrechnung";
    } else if (billingType === BillingTypeEnum.PAPER) {
      titleText = "Papier Abrechnung";
    } else if (billingType === BillingTypeEnum.ZUZAHLUNG) {
      titleText = "Zuzahlungsforderung";
    } else {
      titleText = "Korrekturrechnung";
    }
    // Render

    return (
      <>
        <TopLevelPaper sx={{ borderRadius: "0 0 12px 12px" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container>
              {/* Left side: Search & 'Neu' Button */}
              <Grid item xs={6.5}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ height: 60 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <TextField
                      label="Suche nach Kostenträger-IK oder Name"
                      size="small"
                      value={searchValue}
                      onChange={(e) => {
                        setPage(1);
                        setSearchValue(e.target.value);
                      }}
                      sx={{
                        height: 40,
                        minWidth: 300,
                      }}
                    />
                    {(billStatus === BillingStatus.OPEN ||
                      billStatus === BillingStatus.PREVIOUS) && (
                      <Tooltip title="Nur verzögerte Abrechnungen anzeigen" arrow>
                        <ToggleButton
                          value="delayed"
                          selected={showDelayedOnly}
                          onChange={() => setShowDelayedOnly(!showDelayedOnly)}
                          size="small"
                          sx={{
                            height: 40,
                            borderColor: showDelayedOnly ? "#d32f2f" : undefined,
                            color: showDelayedOnly ? "#d32f2f" : undefined,
                          }}
                        >
                          <PauseCircleOutlineIcon sx={{ mr: 0.5 }} />
                          Verzögert
                        </ToggleButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Neu Abrechnung" arrow>
                      <IconButton
                        onClick={handleNewRechnung}
                        sx={{
                          bgcolor: "success.main",
                          p: 1,
                          mt: 0.5,
                          borderRadius: 2,
                          boxShadow: 1,
                          "&:hover": {
                            bgcolor: "#3b9b6eff",
                          },
                        }}
                      >
                        <PlayCircleIcon sx={{ color: (theme) => theme.palette.grey[100] }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {openPaymentDialog && (
                    <PaymentMethodDialog
                      open={openPaymentDialog}
                      handleClose={handleClosePaymentDialog}
                    />
                  )}
                  {/* Dynamic heading: GKV or Post */}
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "right",
                      fontWeight: "bold",
                      fontSize: "1.6rem",
                      color: "#000",
                      ml: "auto",
                    }}
                  >
                    {titleText}
                  </Typography>
                </Box>
              </Grid>

              {/* Right side: "Edit Selected Bills" button, date pickers */}
              <Grid item xs={5.5}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={2}
                  sx={{ height: 60 }}
                >
                  <Button
                    className="fancy-button"
                    onClick={handleSubmissionForEdit}
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: "capitalize",
                      height: 40,
                      minWidth: 150,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: 0.5,
                    }}
                    disabled={selectedBillIds.length === 0 || billStatus !== BillingStatus.START}
                  >
                    Edit Selected Bills
                  </Button>

                  {/* Date Pickers */}
                  <DatePicker
                    label="Startdatum"
                    value={currentRange.startDate}
                    defaultValue={dayjs()}
                    onChange={(date) => handleStartDateChange(date)}
                    maxDate={currentRange.endDate || dayjs()}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  />
                  <DatePicker
                    label="Enddatum"
                    value={currentRange.endDate}
                    defaultValue={dayjs()}
                    onChange={(date) => handleEndDateChange(date)}
                    minDate={currentRange.startDate || dayjs()}
                    maxDate={dayjs()}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Table / Content */}
            {filteredBills.length > 0 ? (
              <>
                <InsuranceBillsTable
                  bills={filteredBills}
                  billStatus={billStatus}
                  selectedBillIds={selectedBillIds}
                  onSelectAll={handleSelectAll}
                  onSelectOne={handleSelectOne}
                  onEdit={handleEditOne}
                  onDelete={handleRequestDelete}
                  onStatusChange={handleStatusChange}
                  onRegeneratePdfs={handleRegeneratePdfs}
                />

                {/* Delete Confirmation Dialog */}
                <DeleteConfirmationDialog
                  open={deleteDialogOpen}
                  onCancel={handleCancelDelete}
                  onConfirm={handleConfirmDelete}
                  title="Transaktionsbestätigung löschen"
                  content="Möchten Sie die Transaktion wirklich löschen?"
                />

                {/* Pagination */}
                {paginatedData && paginatedData.meta.totalItems > 0 && (
                  <InsuranceBillsPagination
                    totalItems={paginatedData.meta.totalItems}
                    totalPages={paginatedData.meta.totalPages}
                    limit={limit}
                    page={page}
                    onPageChange={setPage}
                    onLimitChange={(newLimit) => {
                      setLimit(newLimit);
                      setPage(1);
                    }}
                  />
                )}
              </>
            ) : (
              <Box
                sx={{
                  ...commonStyles,
                  maxHeight: "70vh",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "12px",
                }}
              >
                <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
                  <Typography margin={2} variant="subtitle1">
                    Für den ausgewählten Status wurden keine Transaktionen gefunden.
                  </Typography>
                </TableContainer>
              </Box>
            )}
          </LocalizationProvider>

          {/* Loading Spinner */}
          {isLoading && <WaitingBackdrop isWorking={isLoading} message="Lade Rechnungen..." />}

          {/* Full Screen Edit Dialog */}
          {isDialogOpen && (
            <BillFullScreenDialog
              open={isDialogOpen}
              fullScreen
              titleComp={"Abrechnung"}
              actions={
                <Button variant="text" onClick={() => handleDialogClose(true)}>
                  Schließen
                </Button>
              }
            >
              {billingType === BillingTypeEnum.PAPER ? (
                <PaperEditFullScreen
                  handleFullScreenDialogClose={handleDialogClose}
                  initialBills={editBills}
                />
              ) : billingType === BillingTypeEnum.ZUZAHLUNG ? (
                <EditCopaymentFullScreen
                  handleFullScreenDialogClose={handleDialogClose}
                  initialBills={editBills}
                />
              ) : billingType === BillingTypeEnum.CORRECTION ? (
                <EditCorrectionBillFullScreen
                  handleFullScreenDialogClose={handleDialogClose}
                  initialBills={editBills}
                />
              ) : billingType === BillingTypeEnum.LINE_ITEM_CORRECTION ? (
                <EditLineItemCorrectionBillFullScreen
                  handleFullScreenDialogClose={handleDialogClose}
                  initialBills={editBills}
                />
              ) : (
                // Old EditFullScreen (commented out — replaced by GkvEditPage)
                // <EditFullScreen
                //   handleFullScreenDialogClose={handleDialogClose}
                //   initialBills={editBills}
                // />
                <GkvEditPage billIds={editBillIds} onClose={handleDialogClose} />
              )}
            </BillFullScreenDialog>
          )}
        </TopLevelPaper>
      </>
    );
  },
);
